import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import type {
  LoyaltyRulesUpdatePayload,
  LoyaltyRulesView,
} from "@/modules/loyalty/domain/types";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { toast } from "sonner";
import { Loader } from "@/shared/ui/Loader";
import { FormRoot, FormPrice, RHF, zodResolver } from "@/shared/forms";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Input } from "@/components/ui/input";



import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  BarChart3Icon,
  ClockIcon,
  InfoIcon,
  SaveIcon,
  SnowflakeIcon,
} from "lucide-react";
import { useEffect } from "react";
import { z } from "zod";
import { loyaltyApi } from "../api/client";
import { loyaltyKeys } from "../api/queries";

const LoyaltyRulesFormSchema = z.object({
  pointsSpendLak: z
    .union([z.number().int().min(0, "ຕ້ອງ ≥ 0"), z.undefined(), z.nan()])
    .optional(),
  pointsEarnedPerUnit: z
    .union([z.number().int().min(1, "ຕ້ອງ ≥ 1"), z.undefined(), z.nan()])
    .optional(),
});

type LoyaltyRulesFormValues = z.infer<typeof LoyaltyRulesFormSchema>;

function toNumOrNull(v: number | "" | undefined): number | null {
  if (v === undefined || v === "" || (typeof v === "number" && Number.isNaN(v)))
    return null;
  return Number(v) as number;
}

export function LoyaltyRulesPage() {
  const qc = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: loyaltyKeys.rules(),
    queryFn: () => loyaltyApi.getRules(),
  });

  const { data: history = [] } = useQuery({
    queryKey: loyaltyKeys.ruleHistory(),
    queryFn: () => loyaltyApi.getRuleHistory(),
  });

  const methods = RHF.useForm<LoyaltyRulesFormValues>({
    resolver: zodResolver(
      LoyaltyRulesFormSchema,
    ) as RHF.Resolver<LoyaltyRulesFormValues>,
    defaultValues: {
      pointsSpendLak: 10_000,
      pointsEarnedPerUnit: 1,
    },
  });

  const pointsSpendLak = methods.watch("pointsSpendLak");
  const pointsEarnedPerUnit = methods.watch("pointsEarnedPerUnit");

  useEffect(() => {
    if (!data) return;
    methods.reset({
      pointsSpendLak: data.pointsSpendLak ?? undefined,
      pointsEarnedPerUnit: data.pointsEarnedPerUnit ?? 1,
    });
  }, [data, methods]);

  const onSubmit = async (values: LoyaltyRulesFormValues) => {
    try {
      const payload: LoyaltyRulesUpdatePayload = {
        pointsSpendLak: toNumOrNull(values.pointsSpendLak),
        pointsEarnedPerUnit: toNumOrNull(values.pointsEarnedPerUnit) ?? 1,
      };
      await loyaltyApi.updateRules(payload);
      toast.success("ບັນທຶກນະໂຍບາຍການເກັບແຕ້ມແລ້ວ");
      qc.invalidateQueries({ queryKey: loyaltyKeys.rules() });
      qc.invalidateQueries({ queryKey: loyaltyKeys.ruleHistory() });
    } catch {
      // fetcher shows toast on error
    }
  };

  const handleDiscard = () => {
    if (data) {
      methods.reset({
        pointsSpendLak: data.pointsSpendLak ?? undefined,
        pointsEarnedPerUnit: data.pointsEarnedPerUnit ?? 1,
      });
    }
  };

  if (isLoading && !data) {
    return (
      <>
        <Header>
          <Breadcrumb
            items={[
              {
                label: "ຕັ້ງຄ່ານະໂຍບາຍ",
                to: "/app/loyalty/settings",
              },
            ]}
          />
        </Header>
        <Main>
          <div className="flex justify-center py-12">
            <Loader />
          </div>
        </Main>
      </>
    );
  }

  const initialData: LoyaltyRulesView = data ?? {
    pointsSpendLak: 10_000,
    pointsEarnedPerUnit: 1,
  };

  if (error && !data) {
    return (
      <>
        <Header>
          <Breadcrumb
            items={[
              {
                label: "ຕັ້ງຄ່ານະໂຍບາຍ",
                to: "/app/loyalty/settings",
              },
            ]}
          />
        </Header>
        <Main>
          <div className="rounded-lg border border-destructive/50 bg-destructive/5 p-4 text-destructive">
            ບໍ່ສາມາດໂຫຼດນະໂຍບາຍການເກັບແຕ້ມໄດ້
          </div>
        </Main>
      </>
    );
  }

  const spendLak =
    typeof pointsSpendLak === "number" && !Number.isNaN(pointsSpendLak)
      ? pointsSpendLak
      : (initialData.pointsSpendLak ?? 10_000);
  const earned =
    typeof pointsEarnedPerUnit === "number" &&
    !Number.isNaN(pointsEarnedPerUnit)
      ? pointsEarnedPerUnit
      : (initialData.pointsEarnedPerUnit ?? 1);

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            {
              label: "ຕັ້ງຄ່ານະໂຍບາຍ",
              to: "/app/loyalty/settings",
            },
          ]}
        />
      </Header>
      <Main>
        <div className="mb-6">
          <Badge variant="secondary" className="mb-2">
            ຕັ້ງຄ່າທົ່ວໄປ
          </Badge>
          <h1 className="font-bold font-heading text-2xl tracking-tight">
            ຕັ້ງຄ່ານະໂຍບາຍການເກັບແຕ້ມ
          </h1>
          <p className="mt-1 text-muted-foreground text-sm">
            ຈັດວ່າລູກຄ້າຈະໄດ້ຮັບແຕ້ມແນວໃດໃນທົ່ວເຄືອຂ່າຍ. ກົດເຫຼົ່ານີ້ໃຊ້ກັບທຸກທຸລະກຳ.
          </p>
        </div>

        <FormRoot<LoyaltyRulesFormValues> methods={methods} onSubmit={onSubmit}>
          <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
            <div className="space-y-6">
              {/* ການສະສົມແຕ້ມ */}
              <Card className="rounded-xl border">
                <CardHeader>
                  <div className="flex items-center gap-2">
                    <span className="flex size-9 items-center justify-center rounded-full bg-primary/10 text-primary">
                      S
                    </span>
                    <div>
                      <h3 className="font-semibold text-lg">ການສະສົມແຕ້ມ</h3>
                      <p className="text-muted-foreground text-sm">
                        ກຳນົດອັດຕາການໃຊ້ຈ່າຍຕໍ່ແຕ້ມ
                      </p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-end gap-3">
                    <div className="min-w-[140px]">
                      <FormPrice
                        name="pointsSpendLak"
                        label="ຈຳນວນໃຊ້ຈ່າຍ (LAK)"
                        placeholder="10000"
                        min={0}
                        currencySymbol="K"
                      />
                    </div>
                    <span className="pb-2 text-muted-foreground">→</span>
                    <div className="min-w-[120px] space-y-2">
                      <label
                        htmlFor="pointsEarnedPerUnit"
                        className="text-muted-foreground text-sm"
                      >
                        ແຕ້ມທີ່ໄດ້ (PT)
                      </label>
                      <RHF.Controller
                        name="pointsEarnedPerUnit"
                        control={methods.control}
                        render={({ field }) => (
                          <Input
                            id="pointsEarnedPerUnit"
                            type="number"
                            min={1}
                            placeholder="1"
                            value={field.value ?? ""}
                            onChange={(e) => {
                              const v = e.target.valueAsNumber;
                              field.onChange(Number.isNaN(v) ? undefined : v);
                            }}
                          />
                        )}
                      />
                    </div>
                    <span className="pb-2 text-muted-foreground text-sm">
                      PT
                    </span>
                  </div>
                  <div className="flex gap-2 rounded-lg border border-blue-200 bg-blue-50 p-3 dark:border-blue-900 dark:bg-blue-950/40">
                    <InfoIcon className="mt-0.5 size-4 shrink-0 text-blue-600 dark:text-blue-400" />
                    <p className="text-muted-foreground text-sm">
                      ກົດປັດຈຸບັນ: ລູກຄ້າຈະໄດ້ຮັບ <strong>{earned} ແຕ້ມ</strong>{" "}
                      ທຸກການໃຊ້ຈ່າຍ{" "}
                      <strong>{spendLak.toLocaleString("lo-LA")} LAK</strong>.
                      ແຕ້ມສ່ວນຍ່ອຍບໍ່ຖືກໃຫ້ໂດຍຄ່າເລີ່ມຕົ້ນ.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* ແຖບຂ້າງ: ຜົນກະທົບ ແລະ ປະຫວັດ */}
            <aside className="space-y-4">
              <Card className="rounded-xl border bg-primary/5 dark:bg-primary/10">
                <CardHeader>
                  <h3 className="font-semibold">ຜົນກະທົບຕໍ່ລະບົບ</h3>
                </CardHeader>
                <CardContent className="space-y-3 text-sm">
                  <div className="flex gap-2">
                    <SnowflakeIcon className="mt-0.5 size-4 shrink-0 text-primary" />
                    <p className="text-muted-foreground">
                      ການປ່ຽນແປງຈະມີຜົນກະທົບຕໍ່ທຸກສາຂາທີ່ໃຊ້ງານທັນທີຫຼັງບັນທຶກ.
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <ClockIcon className="mt-0.5 size-4 shrink-0 text-primary" />
                    <p className="text-muted-foreground">
                      ແຕ້ມທີ່ລູກຄ້າໄດ້ຮັບແລ້ວຈະບໍ່ຖືກຄິດໃໝ່.
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <BarChart3Icon className="mt-0.5 size-4 shrink-0 text-primary" />
                    <p className="text-muted-foreground">
                      ປະມານການອອກແຕ້ມຈະປ່ຽນແປງຕາມອັດຕາໃໝ່.
                    </p>
                  </div>
                </CardContent>
              </Card>

              <Card className="gap-0 rounded-xl border">
                <CardHeader>
                  <h3 className="font-semibold">ປະຫວັດກົດ</h3>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3 text-sm">
                    {history.slice(0, 5).map((entry) => (
                      <li key={entry.id}>
                        <p className="font-medium">{entry.description}</p>
                        <p className="text-muted-foreground text-xs">
                          ໂດຍ {entry.byUserName} •{" "}
                          {new Date(entry.at).toLocaleDateString("lo-LA", {
                            day: "numeric",
                            month: "short",
                            year: "numeric",
                          })}
                        </p>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </aside>
          </div>

          {/* ແຖບປຸ່ມລຸ່ມ */}
          <div className="-mx-4 mt-6 flex flex-wrap items-center justify-end gap-2 border-t bg-background px-4 py-4 sm:-mx-6 sm:px-6">
            <Button type="button" variant="outline" onClick={handleDiscard}>
              ທີ່ງການປ່ຽນແປງ
            </Button>
            <Button type="submit">
              <SaveIcon className="size-4" />
              ບັນທຶກການປ່ຽນແປງ
            </Button>
          </div>
        </FormRoot>
      </Main>
    </>
  );
}
